import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Plus, Trash2, Lock } from 'lucide-react';
import {
  addAufmassZeile,
  berechneAufmass,
  removeAufmassZeile,
  updateAufmassZeile,
} from '@shared/aufmass';
import type { PositionsAufmass } from '@shared/schema';

function parseMenge(value: string): number | undefined {
  if (value.trim() === '') return undefined;
  const n = Number(value.replace(',', '.'));
  return Number.isFinite(n) ? n : undefined;
}

function formatMenge(value: number): string {
  return value.toLocaleString('de-DE', { maximumFractionDigits: 3 });
}

function heute(): string {
  return new Date().toISOString().slice(0, 10);
}

interface AufmassDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  positionNumber?: string | null;
  bezeichnung: string;
  einheit: string;
  sollMenge: number;
  aufmass: PositionsAufmass | null | undefined;
  onSave: (aufmass: PositionsAufmass | null) => void;
}

/**
 * Modal zur Bearbeitung aller Aufmaß-Zeilen einer Position.
 * Änderungen werden lokal gehalten und erst beim Speichern übernommen.
 * Bereits abgerechnete Zeilen sind schreibgeschützt.
 */
export function AufmassDetailModal({
  open,
  onOpenChange,
  positionNumber,
  bezeichnung,
  einheit,
  sollMenge,
  aufmass,
  onSave,
}: AufmassDetailModalProps) {
  const [draft, setDraft] = useState<PositionsAufmass>({ zeilen: [] });

  // Beim Öffnen den aktuellen Stand übernehmen
  useEffect(() => {
    if (open) {
      setDraft(aufmass ? { zeilen: aufmass.zeilen.map((z) => ({ ...z })) } : { zeilen: [] });
    }
  }, [open, aufmass]);

  const summen = berechneAufmass(draft, sollMenge);

  const handleAdd = () => {
    setDraft((prev) => addAufmassZeile(prev, { datum: heute(), menge: 0 }));
  };

  const handleUpdate = (id: string, updates: Partial<{ datum: string; menge: number; notiz: string }>) => {
    setDraft((prev) => updateAufmassZeile(prev, id, updates));
  };

  const handleRemove = (id: string) => {
    setDraft((prev) => removeAufmassZeile(prev, id));
  };

  const handleSave = () => {
    onSave(draft.zeilen.length > 0 ? draft : null);
    onOpenChange(false);
  };

  const warnClass =
    summen.warnstufe === 'ueber' || summen.warnstufe === 'voll'
      ? 'text-red-600'
      : summen.warnstufe === 'warnung'
        ? 'text-amber-600'
        : 'text-emerald-600';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            Aufmaß {positionNumber ? `· Pos. ${positionNumber}` : ''}
          </DialogTitle>
          <p className="truncate text-sm text-gray-500" title={bezeichnung}>
            {bezeichnung}
          </p>
        </DialogHeader>

        {/* Live-Summen */}
        <div className="grid grid-cols-4 gap-3 rounded-lg border bg-gray-50 px-4 py-3 text-sm">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-gray-500">Soll</p>
            <p className="font-semibold tabular-nums">{formatMenge(sollMenge)} {einheit}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-gray-500">Ist</p>
            <p className="font-semibold tabular-nums">{formatMenge(summen.istMenge)} {einheit}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-gray-500">Rest</p>
            <p className="font-semibold tabular-nums">{formatMenge(summen.restMenge)} {einheit}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-gray-500">Auslastung</p>
            <p className={`font-semibold tabular-nums ${warnClass}`}>
              {formatMenge(summen.auslastungProzent)} %
            </p>
          </div>
        </div>

        {/* Zeilen */}
        <div className="min-h-0 flex-1 overflow-auto">
          {draft.zeilen.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">
              Noch keine Aufmaß-Einträge. Fügen Sie eine Zeile hinzu.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-100 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-2 py-2 w-[150px]">Datum</th>
                  <th className="px-2 py-2 w-[120px] text-right">Menge</th>
                  <th className="px-2 py-2">Notiz</th>
                  <th className="px-2 py-2 w-[70px] text-center">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {draft.zeilen.map((zeile) => {
                  const gesperrt = Boolean(zeile.abgerechnet);
                  return (
                    <tr key={zeile.id} className={`border-b border-gray-100 ${gesperrt ? 'bg-gray-50' : ''}`}>
                      <td className="px-2 py-1.5">
                        <Input
                          type="date"
                          value={zeile.datum ?? ''}
                          disabled={gesperrt}
                          onChange={(e) => handleUpdate(zeile.id, { datum: e.target.value })}
                          className="h-8 text-sm"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input
                          type="text"
                          inputMode="decimal"
                          defaultValue={String(zeile.menge).replace('.', ',')}
                          disabled={gesperrt}
                          onChange={(e) => {
                            const parsed = parseMenge(e.target.value);
                            handleUpdate(zeile.id, { menge: parsed ?? 0 });
                          }}
                          className="h-8 text-right text-sm"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input
                          type="text"
                          defaultValue={zeile.notiz ?? ''}
                          disabled={gesperrt}
                          placeholder="Notiz (optional)"
                          onChange={(e) => handleUpdate(zeile.id, { notiz: e.target.value })}
                          className="h-8 text-sm"
                        />
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        {gesperrt ? (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-400" title="Bereits abgerechnet">
                            <Lock className="h-3.5 w-3.5" />
                          </span>
                        ) : (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemove(zeile.id)}
                            className="h-8 w-8 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50"
                            title="Zeile löschen"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div>
          <Button type="button" variant="outline" size="sm" onClick={handleAdd} className="gap-1.5">
            <Plus className="h-4 w-4" />
            Zeile hinzufügen
          </Button>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button onClick={handleSave} className="bg-teal-600 hover:bg-teal-700">
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
